import React, { useCallback, useEffect, useMemo } from 'react'
import { useQuery, useReactiveVar } from '@apollo/client'

import { LoadMore, NoItems } from 'components'
import { ProjectsList, SkeletonLoader } from 'modules/Projects/components'

import {
  projectsFiltersVar,
  projectsListVar,
  projectsOffsetVar,
  projectsTotalVar,
  sortTypeVar
} from 'modules/Projects/store'
import { GET_PROJECTS } from 'modules/Projects/graphql/queries/getProjects'
import { GET_SPACES } from 'modulesCommon/graphql/queries'
import { DEFAULT_LIMIT } from 'modules/Projects/constants'

import { countryCodeVar, labelCodeIdVar, spaceIdVar } from 'modulesCommon/store'
import { getSpaceUnits, getUnits } from 'shared/utils/labels'
import { useGpsUnits, useZonesList } from 'modulesCommon/serviceHooks'
import { useLoading } from 'shared/hooks'

import { ISpace } from 'shared/interfaces'

const ProjectsListContainer = () => {
  const sortType = useReactiveVar(sortTypeVar)
  const projectsList = useReactiveVar(projectsListVar)
  const projectsTotal = useReactiveVar(projectsTotalVar)
  const projectsOffset = useReactiveVar(projectsOffsetVar)
  const labelCodeId = useReactiveVar(labelCodeIdVar)
  const spaceId = useReactiveVar(spaceIdVar)
  const countryCode = useReactiveVar(countryCodeVar)
  const projectsFilters = useReactiveVar(projectsFiltersVar)
  const { zoneList } = useZonesList()
  const { gpsUnitsLoading } = useGpsUnits()

  const { label_id, ...restProjectsFilters } = projectsFilters

  const { loading, fetchMore, refetch } = useQuery(GET_PROJECTS, {
    variables: {
      offset: 0,
      limit: DEFAULT_LIMIT,
      sort: sortType,
      filters: {
        ...restProjectsFilters,
        countryCode,
        ...(spaceId && !labelCodeId && !label_id.length && { space: spaceId }),
        ...(label_id?.length && label_id !== 'all' && { label_id: label_id }),
        ...(labelCodeId && !spaceId && { label_id: labelCodeId })
      }
    },
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      projectsListVar([...(data.getCampaigns?.artists || [])])
      projectsTotalVar(parseFloat(data.getCampaigns?.total) || 0)
    }
  })

  const increaseProjectsOffset = useCallback(() => {
    if (!loading && projectsOffset !== projectsList.length) {
      projectsOffsetVar(projectsList.length)
      fetchMore({ variables: { offset: projectsList.length } })
    }
  }, [fetchMore, projectsList.length, projectsOffset, loading])

  const { data: spacesData } = useQuery(GET_SPACES)

  const units = useMemo(() => {
    if (!spaceId) {
      return getUnits(zoneList, labelCodeId) || []
    }
    const currentSpace = spacesData?.getSpaces.find((space: ISpace) => space.id === spaceId)
    return getSpaceUnits(zoneList, currentSpace)
  }, [zoneList, labelCodeId, spaceId, spacesData])

  useLoading({ isLoading: loading || gpsUnitsLoading })

  useEffect(() => {
    projectsOffsetVar(0)
    projectsListVar([])
    refetch && refetch()
  }, [refetch, sortType, countryCode, projectsFilters])

  return (
    <>
      {!projectsList.length && loading && <SkeletonLoader />}
      {!projectsList.length && <NoItems name="projects" />}
      {projectsList.length ? (
        <>
          <ProjectsList units={units} projects={projectsList} />
          <LoadMore
            onLoad={increaseProjectsOffset}
            isItemsLoading={loading}
            itemsTotal={projectsTotal}
            itemsCount={projectsList.length}
          />
        </>
      ) : null}
    </>
  )
}

export default ProjectsListContainer
